import { ref, computed } from 'vue'
import { useApiClient } from './useApiClient'

export const useNotification = () => {
  const apiClient = useApiClient()
  
  const notifications = ref<any[]>([])
  const loading = ref(false)
  const hasMore = ref(true)
  const currentPage = ref(1)
  const unreadCount = ref(0)
  const pageSize = 10

  const hasUnread = computed(() => {
    return notifications.value.some(n => !n.isRead)
  })

  const loadNotifications = async (page: number = 1) => {
    if (loading.value) return

    loading.value = true
    try {
      const response = await apiClient.getNotifications(page, pageSize)
      
      if (page === 1) {
        notifications.value = response.data || []
      } else {
        notifications.value.push(...(response.data || []))
      }

      hasMore.value = (response.data?.length || 0) === pageSize
      currentPage.value = page
    } catch (error) {
      console.error('Error loading notifications:', error)
    } finally {
      loading.value = false
    }
  }

  const loadMoreNotifications = () => {
    if (hasMore.value && !loading.value) {
      loadNotifications(currentPage.value + 1)
    }
  }

  const markAsRead = async (notification: any) => {
    if (notification.isRead) return

    try {
      await apiClient.notificationMarkAsRead(notification.id)
      notification.isRead = true
      await loadUnreadCount()
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const markAllAsRead = async () => {
    try {
      await apiClient.notificationMarkAllAsRead()
      notifications.value.forEach(n => n.isRead = true)
      unreadCount.value = 0
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }

  const loadUnreadCount = async () => {
    try {
      const count = await apiClient.notificationCountUnRead()
      unreadCount.value = count || 0
    } catch (error) {
      console.error('Error loading unread count:', error)
    }
  }

  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now.getTime() - date.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 1) {
      return 'Hôm qua'
    } else if (diffDays < 7) {
      return `${diffDays} ngày trước`
    } else {
      return date.toLocaleDateString('vi-VN')
    }
  }

  return {
    notifications,
    loading,
    hasMore,
    hasUnread,
    unreadCount,
    loadNotifications,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
    loadUnreadCount,
    formatDate
  }
} 